import { getAuthDashMetaCsrfToken } from '@revelio/auth';

import { dashMetaFetch } from '../../../../../shared/api/dash-meta-fetch';
import type {
  SourceWhaleCampaign,
  SourceWhaleProject,
  SWCandidate,
} from './source-whale.model';

function getErrorMessage(errorData: unknown, fallback: string): string {
  if (typeof errorData !== 'object' || errorData === null) {
    return fallback;
  }
  if (!('message' in errorData)) {
    return fallback;
  }

  const { message } = errorData;
  if (typeof message === 'string' && message.length > 0) {
    return message;
  }
  if (Array.isArray(message)) {
    const parts = message.filter(
      (item): item is string => typeof item === 'string' && item.length > 0
    );
    if (parts.length > 0) {
      return parts.join(' ');
    }
  }

  return fallback;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status >= 200 && response.status <= 299) {
    const data = await response.json();
    return data;
  }

  const fallbackMessage = response.statusText || 'An error occurred';
  const errorData: unknown = await response.json().catch(() => null);
  throw new Error(getErrorMessage(errorData, fallbackMessage));
}

export const getSourceWhaleCampaigns = async ({
  apiKey,
}: {
  apiKey: string;
}) => {
  const csrfToken = getAuthDashMetaCsrfToken();
  if (!csrfToken) throw new Error('Missing CSRF token');

  return handleResponse<{
    campaigns: { campaigns: SourceWhaleCampaign[] };
  }>(
    await dashMetaFetch('/api/source-whale/campaigns/list', {
      method: 'GET',
      headers: { 'source-whale-api-key': apiKey },
    })
  );
};

export const getSourceWhaleProjects = async ({
  apiKey,
}: {
  apiKey: string;
}) => {
  const csrfToken = getAuthDashMetaCsrfToken();
  if (!csrfToken) throw new Error('Missing CSRF token');

  return handleResponse<{ projects: SourceWhaleProject[] }>(
    await dashMetaFetch('/api/source-whale/projects/list', {
      method: 'GET',
      headers: { 'source-whale-api-key': apiKey },
    })
  );
};

export const addSourceWhaleCandidates = async ({
  apiKey,
  candidates,
  campaignId,
  projectIds,
}: {
  apiKey: string;
  candidates: SWCandidate[];
  campaignId?: string;
  projectIds?: string[];
}) => {
  const csrfToken = getAuthDashMetaCsrfToken();
  if (!csrfToken) throw new Error('Missing CSRF token');

  const endpoint = campaignId ? 'candidates/add-to-campaign' : 'candidates/add';

  return handleResponse<{
    candidateIds: string[];
    url?: string;
  }>(
    await dashMetaFetch(`/api/source-whale/${endpoint}`, {
      method: 'POST',
      headers: { 'source-whale-api-key': apiKey },
      body: JSON.stringify({
        candidates,
        ...(campaignId && { campaignId, sendImmediately: false }),
        ...(projectIds && { projectIds }),
      }),
    })
  );
};
